import React from "react";
import { ArrowRight } from "lucide-react";
import { simulation, media } from "../../content/siteContent";
import { SectionHeading } from "./primitives";
import { Reveal } from "../motion/Reveal";

const toneColor = {
  good: "text-teal",
  warn: "text-gold",
  bad: "text-[#E8654F]",
};

const DailyReport = () => {
  const { dailyReport } = simulation;
  return (
    <div
      data-testid="daily-operating-report"
      className="border border-hair-dark bg-navy/60 backdrop-blur-sm rounded-sm overflow-hidden"
    >
      <div className="flex items-center justify-between border-b border-hair-dark px-6 py-4">
        <div>
          <p className="eyebrow text-teal">{dailyReport.title}</p>
          <p className="mt-1 text-white/50 text-xs">{dailyReport.note}</p>
        </div>
        <span className="font-display font-bold text-white text-xs border border-hair-dark rounded-sm px-2.5 py-1">
          {dailyReport.shift}
        </span>
      </div>
      <div className="divide-y divide-hair-dark/60">
        {dailyReport.kpis.map((k) => (
          <div key={k.label} className="flex items-center justify-between px-6 py-3">
            <span className="text-white/70 text-sm">{k.label}</span>
            <span className={`font-display font-bold text-sm ${toneColor[k.tone] || "text-white"}`}>
              {k.value}
            </span>
          </div>
        ))}
      </div>
    </div>
  );
};

export const Simulation = () => (
  <section
    id="simulation"
    data-testid="simulation-section"
    className="relative bg-navy py-24 lg:py-32 overflow-hidden grain"
  >
    <div className="absolute inset-0">
      <img
        src={media.simulation}
        alt=""
        aria-hidden="true"
        loading="lazy"
        className="h-full w-full object-cover opacity-20"
      />
      <div className="absolute inset-0 bg-navy/85" />
      <div className="absolute inset-0 blueprint" />
    </div>

    <div className="container-page relative">
      <SectionHeading eyebrow={simulation.eyebrow} dark>
        {simulation.heading}
      </SectionHeading>

      <div className="mt-8 grid lg:grid-cols-12 gap-12 lg:gap-16">
        <div className="lg:col-span-5">
          {simulation.body.map((p, i) => (
            <Reveal key={i} delay={i * 0.1}>
              <p className="text-white/75 text-base lg:text-lg leading-relaxed mb-5">{p}</p>
            </Reveal>
          ))}
          <Reveal delay={0.2}>
            <blockquote className="mt-8 border-l-2 border-gold pl-6">
              <p className="font-display font-bold text-white text-xl lg:text-2xl tracking-tight leading-snug">
                “{simulation.quote}”
              </p>
            </blockquote>
          </Reveal>
        </div>

        <div className="lg:col-span-7">
          <Reveal>
            <DailyReport />
          </Reveal>
        </div>
      </div>

      {/* From evidence to capability */}
      <div className="mt-16" data-testid="evidence-to-capability">
        <Reveal>
          <p className="eyebrow text-gold">{simulation.evidenceToCapability.heading}</p>
        </Reveal>
        <div className="mt-6 grid sm:grid-cols-2 lg:grid-cols-6 gap-3">
          {simulation.evidenceToCapability.steps.map((s, i) => (
            <Reveal
              key={s.n}
              delay={i * 0.05}
              className="relative border border-hair-dark bg-navy-2/60 backdrop-blur-sm p-4 hover:border-teal/60 transition-colors duration-300"
            >
              <div className="flex items-center justify-between">
                <span className="font-display font-bold text-teal text-sm">{s.n}</span>
                {i < simulation.evidenceToCapability.steps.length - 1 && (
                  <ArrowRight className="h-3.5 w-3.5 text-white/30 hidden lg:block" />
                )}
              </div>
              <p className="mt-3 text-white/80 text-xs leading-snug">{s.label}</p>
            </Reveal>
          ))}
        </div>
      </div>

      {/* Worked vignette */}
      <Reveal delay={0.1}>
        <div className="mt-10 border border-hair-dark bg-navy-2/50 backdrop-blur-sm rounded-sm p-6 lg:p-8">
          <p className="eyebrow text-teal">{simulation.vignette.tag}</p>
          <p className="mt-4 text-white/75 text-sm lg:text-base leading-relaxed max-w-3xl">
            {simulation.vignette.body}
          </p>
        </div>
      </Reveal>
    </div>
  </section>
);
